import { checkTypeExhausted, onlyUnique } from "Utility";
import { getPlayConditionFormatKey } from "./PlayCondition";
import { MatchSet, MatchSetType, SET_TYPE_ABBREVIATIONS } from "./Set/MatchSet";
import { FsSetPoints } from "db/model/match/FsMatchFormat";

export type MatchFormatType = "Single" | "BestOf" | "FirstTo" | "PlayAll";

export const isMatchFormatType = (
  value: string | undefined
): value is MatchFormatType => {
  if (!value) {
    return false;
  }

  return ["Single", "BestOf", "FirstTo", "PlayAll"].includes(value);
};

export type MatchFormat = SingleSetFormat | MultiSetFormat;

export type SingleSetFormat = {
  type: "Single";
};

export type MultiSetFormat = {
  amount: number;
  setAdvantageRosterIds: string[] | undefined;
  setPoints?: FsSetPoints;
  tiebreakerSet: boolean;
  type: "BestOf" | "FirstTo" | "PlayAll";
};

export const hasSetAdvantage = (format: MatchFormat) => {
  if (format.type === "Single") {
    return false;
  }

  return !!format.setAdvantageRosterIds?.length;
};

export const getMultiSetFormatString = (format: MultiSetFormat) => {
  switch (format.type) {
    case "BestOf":
      return `Best Of ${format.amount}`;
    case "FirstTo":
      return `First To ${format.amount}`;
    case "PlayAll":
      return `Play All ${format.amount}`;
    default:
      return checkTypeExhausted(format.type);
  }
};

export const getMatchFormatKeys = (format: MatchFormat, sets: MatchSet[]) => {
  const nonTbSets = sets.filter((s) => !s.isTiebreaker);
  const setOne = sets.at(0);
  if (!setOne) {
    return ["Unknown"];
  }

  const setType = setOne.type;
  const allSetTypesSame = nonTbSets.every((s) => s.type === setType);
  if (!allSetTypesSame) {
    return ["Varies"];
  }

  const formatKeys = [SET_TYPE_ABBREVIATIONS[setType]];
  const formatAmountKey = getFormatAmountKey(format);
  if (!!formatAmountKey) {
    formatKeys.push(formatAmountKey);
  }

  const driversPerTeam = setOne.format.driversPerTeam;
  const allDriversPerTeamSame = nonTbSets.every(
    (s) => s.format.driversPerTeam === driversPerTeam
  );
  if (!allDriversPerTeamSame) {
    formatKeys.push("Varies");
    return formatKeys;
  }

  if (showDriversPerTeamKey(driversPerTeam, setType)) {
    formatKeys.push(getDriversPerTeamKey(driversPerTeam));
  }

  const setSpecificKeys = nonTbSets.map(getSetSpecificKey).filter(onlyUnique);
  if (setSpecificKeys.length !== 1) {
    formatKeys.push("Varies");
  } else {
    formatKeys.push(setSpecificKeys[0]);
  }

  return formatKeys;
};

const getFormatAmountKey = (format: MatchFormat) => {
  switch (format.type) {
    case "Single":
      return undefined;
    case "BestOf":
      return `BO${format.amount}`;
    case "FirstTo":
      return `Ft${format.amount}`;
    case "PlayAll":
      return `PA${format.amount}`;
    default:
      return checkTypeExhausted(format);
  }
};

const getDriversPerTeamKey = (driversPerTeam: number) => {
  switch (driversPerTeam) {
    case 1:
      return "SOLO";
    case 2:
      return "DUO";
    case 3:
      return "TRIO";
    case 4:
      return "QUAD";
    default:
      return `${driversPerTeam}s`;
  }
};

const showDriversPerTeamKey = (driversPerTeam: number, type: MatchSetType) => {
  switch (type) {
    case "ForwardCupMode":
    case "ForwardCupModeLong":
    case "Knockout":
    case "ReverseCupMode":
    case "Rounds":
    case "RoundsKnockout":
    case "TimeAttack":
      return driversPerTeam !== 1;
    case "WorldTour":
      return driversPerTeam !== 2;
    case "Matchmaking":
      return true;
    default:
      checkTypeExhausted(type);
      return false;
  }
};

const getSetSpecificKey = (set: MatchSet) => {
  switch (set.type) {
    case "ForwardCupMode":
      return set.format.pointsLimit.toString();
    case "ForwardCupModeLong":
      return `${getPlayConditionFormatKey(
        set.format.playCondition,
        set.format.numTracks
      )}`;
    //-${set.format.pointsLimit}`;
    case "Knockout":
    case "RoundsKnockout":
      return set.format.startingLives.toString();
    case "ReverseCupMode":
      return set.format.startingPoints.toString();
    case "Rounds":
      return `${set.format.numTracks} x ${set.format.roundsPerTrack}`;
    case "TimeAttack":
      return ``;
    case "Matchmaking":
    case "WorldTour":
      return getPlayConditionFormatKey(
        set.format.playCondition,
        set.format.numTracks
      );
    default:
      return checkTypeExhausted(set);
  }
};
